/**
 * File: tableUtilities.js
 * Origin: isotect
 * Description: Functions that process table operations.
 *
 * Version: v1.1.1
 * Created: 2026-06-23
 * Last Updated: 2026-07-25
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import * as xlsx from "xlsx";

/**
 * Counts rows where the specified column matches the given condition.
 *
 * @param {Array<Object>} rows - Rows to evaluate
 * @param {string} column - Column name to evaluate
 * @param {*} condition - Expected column value
 *
 * @returns {number} Number of matching rows
 */
function countMatchingRows(rows, column, condition) {
    let count = 0;

    rows.forEach((row) => {
        if (matchesCondition(row, column, condition)) {
            count += 1;
        }
    });

    return count;
}

/**
 * Exports an HTML table to an Excel (.xlsx) file.
 *
 * @param {string} tableId - The ID of the HTML <table> element to export.
 *
 * @returns {void}
 *
 * @throws {Error} If the table element cannot be found
 *
 * @example
 * exportTableToExcel("myTable");
 */
function exportTableToExcel(tableId) {
    const table = document.getElementById(tableId);

    if (!table) {
        throw new Error(`Table with id "${tableId}" not found.`);
    }

    const workbook = xlsx.utils.table_to_book(table, {
        sheet: "Sheet1",
    });

    xlsx.writeFile(workbook, "table.xlsx");
}

/**
 * Returns all rows where the specified column matches the given condition.
 *
 * @param {Array<Object>} rows - Rows to filter
 * @param {string} column - Column name to evaluate
 * @param {*} condition - Expected column value
 *
 * @returns {Array<Object>} Matching rows
 */
function filterTableRowsByCriteria(rows, column, condition) {
    return rows.filter((row) => matchesCondition(row, column, condition));
}

/**
 * Returns all rows that match every provided filter.
 *
 * @param {Array<Object>} rows - Rows to filter
 * @param {Object.<string, *>} filters - Object mapping column names to their expected values
 *
 * @returns {Array<Object>} Filtered rows
 */
function filterTableRows(rows, filters) {
    const filtersToApply = Object.entries(filters);

    filtersToApply.forEach(([key, value]) => {
        rows = filterTableRowsByCriteria(rows, key, value);
    });

    return rows;
}

/**
 * Determines whether a row's column value matches the condition
 *
 * @param {Object} row - Row to evaluate
 * @param {string} column - Column name to evaluate
 * @param {*} condition - Expected column value
 *
 * @returns {boolean} True if the row matches
 */
function matchesCondition(row, column, condition) {
    return row[column] === condition;
}

/**
 * Searches table rows for a query string across all column values.
 *
 * Performs a case-insensitive partial match search using `includes`.
 * Matches any row where at least one column value contains the query.
 *
 * @param {Array<Object>} rows - Array of table row objects
 * @param {string} query - Search string to match against row values
 *
 * @returns {Array<Object>} Array of rows matching the search query
 */
function searchTableRows(rows, query) {
    const normalizedQuery = query.toLowerCase();

    return rows.filter((row) =>
        Object.values(row).some((value) =>
            String(value).toLowerCase().includes(normalizedQuery),
        ),
    );
}

/**
 * Sorts an array of table rows by a specified column.
 *
 * Creates a new sorted array without mutating the original input.
 * Supports ascending and descending order using standard value comparison.
 *
 * @param {Array<Object>} rows - Array of table row objects to sort
 * @param {string} column - The key in each row object to sort by
 * @param {boolean} [isAscending] - Whether to sort in ascending order
 *
 * @returns {Array<Object>} A new array of sorted rows
 */
function sortTableRows(rows, column, isAscending = true) {
    return [...rows].sort((firstRow, secondRow) => {
        const firstValue = firstRow[column];
        const secondValue = secondRow[column];

        if (firstValue > secondValue) {
            return isAscending ? 1 : -1;
        }

        if (secondValue > firstValue) {
            return isAscending ? -1 : 1;
        }

        return 0;
    });
}

/**
 * Transposes table data by converting the header row into the first column
 * and reorganizing row values into columns.
 *
 * @param {Array} headers - Array of header values
 * @param {Array<Array>} rows - 2D array representing table rows
 *
 * @returns {Array<Array>} Transposed matrix
 */
function transposeTable(headers, rows) {
    const numColumns = rows.length + 1;
    const numRows = headers.length;

    const originalMatrix = [headers];
    let transposeMatrix = [];

    for (let i = 0; i < rows.length; i++) {
        originalMatrix.push(rows[i]);
    }

    for (let i = 0; i < numRows; i++) {
        const row = [];
        for (let j = 0; j < numColumns; j++) {
            row.push("");
        }
        transposeMatrix.push(row);
    }

    for (let i = 0; i < originalMatrix.length; i++) {
        let row = originalMatrix[i];
        for (let j = 0; j < row.length; j++) {
            transposeMatrix[j][i] = originalMatrix[i][j];
        }
    }

    return transposeMatrix;
}

export {
    countMatchingRows,
    exportTableToExcel,
    filterTableRows,
    filterTableRowsByCriteria,
    matchesCondition,
    searchTableRows,
    sortTableRows,
    transposeTable,
};
